import { droneFeedTypeLabel, isTerminalMissionStatus, type MissionStatus } from '@tsr/shared-types';

/**
 * Drone D1 #319 mission-status presentation (#321 adds confirmed + enroute). COLOUR DISCIPLINE
 * (design-council Law 1): the COP reserves red + green as MAP-signal hues — these are side-panel
 * / admin badges, so the existing precedent (DroneCoverageBody, incidentStyles) applies: amber
 * for pending / attention, emerald for the positive accepted state, indigo/cyan/sky for the
 * dispatch-progress states (confirmed → enroute → flown), violet/teal for the money states, and
 * SLATE (never red) for the terminal declined / cancelled states. The progression reads
 * amber→emerald→indigo→cyan→sky→violet→teal. Every status carries an explicit text label, so hue
 * is never load-bearing alone.
 */
export const MISSION_STATUS_BADGE: Record<MissionStatus, { label: string; cls: string }> = {
  requested: { label: 'Requested', cls: 'bg-amber-500/20 text-amber-300' },
  accepted: { label: 'Accepted', cls: 'bg-emerald-500/20 text-emerald-300' },
  confirmed: { label: 'Confirmed', cls: 'bg-indigo-500/20 text-indigo-300' },
  enroute: { label: 'Enroute', cls: 'bg-cyan-500/20 text-cyan-300' },
  flown: { label: 'Flown', cls: 'bg-sky-500/20 text-sky-300' },
  billed: { label: 'Billed', cls: 'bg-violet-500/20 text-violet-200' },
  paid: { label: 'Paid', cls: 'bg-teal-500/20 text-teal-200' },
  declined: { label: 'Declined', cls: 'bg-slate-600/40 text-slate-300' },
  cancelled: { label: 'Cancelled', cls: 'bg-slate-600/40 text-slate-300' },
};

export function missionStatusBadge(status: string): { label: string; cls: string } {
  return (
    MISSION_STATUS_BADGE[status as MissionStatus] ?? {
      label: status,
      cls: 'bg-slate-600/40 text-slate-300',
    }
  );
}

/**
 * Operator-facing labels for a mission's usage-ledger `status` (W6 raw-enum legibility,
 * ledger §3.D). Rendered as "Billing: <status>" so the internal table name "ledger" never
 * surfaces; the status words (pending / billed / paid) are already plain English.
 */
export const LEDGER_STATUS_LABEL: Record<string, string> = {
  pending: 'pending',
  billed: 'billed',
  paid: 'paid',
};

/** "Billing: pending" — the humanized billing line for a mission's ledger status; an
 *  unrecognised status falls back to the (already plain-word) value itself. */
export function missionBillingLabel(status: string): string {
  return `Billing: ${LEDGER_STATUS_LABEL[status] ?? status}`;
}

/**
 * The happy-path milestones a requestor tracks at a glance (#374) — the SAME chain as the
 * DroneMissionDetailBody lifecycle timeline (Requested → Accepted → Confirmed → Enroute → Flown),
 * so the rail stepper and the detail panel never disagree. The post-flight bookkeeping states
 * (billed / paid) read as the final 'Flown' milestone (the flight happened). Terminal off-ramps
 * (declined / cancelled) leave the path, so they have NO progress index — the status badge carries
 * them instead.
 */
export const MISSION_PROGRESS_STEPS = [
  'Requested',
  'Accepted',
  'Confirmed',
  'Enroute',
  'Flown',
] as const;

/**
 * The furthest progress step a mission has reached (0-based into MISSION_PROGRESS_STEPS), or null
 * for an off-track terminal state (declined / cancelled) that never completed the path. enroute =
 * 'Enroute' (dispatched / on station); flown / billed / paid all map to the final 'Flown'
 * milestone — the flight happened, and billing is post-flight bookkeeping, not a coverage step.
 */
export function missionProgressIndex(status: MissionStatus): number | null {
  switch (status) {
    case 'requested':
      return 0;
    case 'accepted':
      return 1;
    case 'confirmed':
      return 2;
    case 'enroute':
      return 3;
    case 'flown':
    case 'billed':
    case 'paid':
      return 4;
    case 'declined':
    case 'cancelled':
      return null;
  }
}

/** True for the requestor's OPEN (still-in-flight) requests — not a terminal off-ramp/closeout.
 *  Drives the "Requests" tab count badge so a closed mission stops counting. */
export function isOpenMission(status: MissionStatus): boolean {
  return !isTerminalMissionStatus(status);
}

/** A compact human summary of the #321 dispatch terms (eta / on-station window / quoted price /
 *  feed kind), or null when the operator has not accepted-with-terms yet. Shared by both panels. */
export function missionTermsSummary(m: {
  eta_minutes: number | null;
  quoted_price_cad: number | null;
  feed_type: string | null;
  offered_on_station_minutes?: number | null;
}): string | null {
  const parts: string[] = [];
  if (m.eta_minutes != null) parts.push(`ETA ${m.eta_minutes} min`);
  // The operator's committed eyes-on duration (#345) — how long they'll hold eyes-on.
  if (m.offered_on_station_minutes != null) {
    parts.push(`eyes-on ${m.offered_on_station_minutes} min`);
  }
  // A $0 quote = a government / public-service ($0-rated) asset (#343) — show "No charge"
  // rather than a bare "$0 CAD".
  if (m.quoted_price_cad != null) {
    parts.push(m.quoted_price_cad === 0 ? 'No charge' : `$${m.quoted_price_cad.toLocaleString()} CAD`);
  }
  if (m.feed_type) parts.push(droneFeedTypeLabel(m.feed_type));
  return parts.length > 0 ? parts.join(' · ') : null;
}

/** The on-station coverage window for the drones-on-incident view: the operator's committed
 *  (offered) window once accepted, else the requestor's requested window, else null. PURE. */
export function missionOnStationMinutes(m: {
  requested_on_station_minutes: number | null;
  offered_on_station_minutes: number | null;
}): number | null {
  return m.offered_on_station_minutes ?? m.requested_on_station_minutes ?? null;
}

/** Safely read the original area-of-interest point off a mission's requested_capabilities jsonb
 *  (carried from the #320 match). Used as the operator-side location fallback (Phase 4e) when a
 *  request isn't incident-linked, and by the mission detail's "request another drone" affordance. */
export function aoFromCapabilities(
  caps: Record<string, unknown> | null | undefined,
): { lat: number; lon: number } | null {
  const ao = caps?.ao;
  if (ao && typeof ao === 'object') {
    const { lat, lon } = ao as { lat?: unknown; lon?: unknown };
    if (typeof lat === 'number' && typeof lon === 'number') return { lat, lon };
  }
  return null;
}

/** A compact human summary of a mission's requested_capabilities jsonb. */
export function capabilitiesSummary(caps: Record<string, unknown> | null | undefined): string {
  if (!caps) return '—';
  const sensors = Array.isArray(caps.required_sensors) ? (caps.required_sensors as string[]) : [];
  const platform = typeof caps.platform_category === 'string' ? caps.platform_category : null;
  const parts: string[] = [];
  if (sensors.length > 0) parts.push(sensors.join(', '));
  if (platform) parts.push(platform);
  return parts.length > 0 ? parts.join(' · ') : 'No specific capabilities';
}
